import { openDB, type IDBPDatabase } from 'idb'
import { create_UUID } from '@/utils/uuid'
import SaveWorker from '@/indexedDB/worker.js?worker'

const getDBName = (userId: number | null) => `gisuz_${userId || 'guest'}`

export const DB_VERSION = 1

function getErrorName(error: unknown): string {
    if (error instanceof DOMException || error instanceof Error) {
        return error.name
    }

    return ''
}

interface AppMetadata {
    id: string
    lastSync: Date
    dbVersion: number
    userId: number | null
    dbName?: string
}

export interface Cemetery {
    cemetery_id: number
    guid: string
    access_revoked?: boolean
    [key: string]: any
}

export interface CemeteryBurial {
    burial_id?: number
    cemetery_id?: number
    card_id?: number
    [key: string]: any
}

export interface CemeteryPointOfUser {
    load_detail_id?: number
    card_id?: number
    [key: string]: any
}

export interface CemeteryPoint {
    id: number
    card_id: number
    cemetery_id: number
    cemetery_name: string
    photos: Array<{ name: string }>
    coordinates: number[] | null
    description: string
    date: string
    burial_length: number
    burial_width: number
    access_revoked: boolean
    [key: string]: any
}

export interface UploadedDates {
    cemetery_id: number
    photo_count: number
    uploaded_last_date: string
}

export interface UploadedPoints {
    cemetery_id: number
    point_ids: number[]
    last_upload: Date
}

export interface UploadedPhotos {
    cemetery_id: number
    photo_names: string[]
    last_upload: Date
}

export interface UploadedPositions {
    cemetery_id: number
    center_map: number[]
    zoom_map: number
}

class IndexedDB {
    private db: IDBPDatabase | null = null
    private currentUserId: number | null = null
    private initialized = false

    isInitialized(): boolean {
        return this.initialized && this.db !== null
    }

    async init(userId: number | null): Promise<void> {
        try {
            this.initialized = false
            this.currentUserId = userId

            this.db = await openDB(getDBName(userId), DB_VERSION, {
                upgrade(db) {
                    if (!db.objectStoreNames.contains('metadata')) {
                        db.createObjectStore('metadata', {
                            keyPath: 'id'
                        })
                    }

                    if (!db.objectStoreNames.contains('cemetery')) {
                        db.createObjectStore('cemetery', {
                            keyPath: 'cemetery_id'
                        })
                    }

                    if (!db.objectStoreNames.contains('cemetery_burials')) {
                        const burialsStore = db.createObjectStore(
                            'cemetery_burials',
                            {
                                keyPath: 'burial_id'
                            }
                        )

                        burialsStore.createIndex(
                            'card_id',
                            'card_id'
                        )
                    }

                    if (!db.objectStoreNames.contains('cemetery_points_of_user')) {
                        const pointsOfUserStore = db.createObjectStore(
                            'cemetery_points_of_user',
                            {
                                keyPath: 'load_detail_id'
                            }
                        )

                        pointsOfUserStore.createIndex(
                            'card_id',
                            'card_id'
                        )
                    }

                    if (!db.objectStoreNames.contains('cemetery_points')) {
                        const pointsStore = db.createObjectStore(
                            'cemetery_points',
                            {
                                keyPath: 'id'
                            }
                        )

                        pointsStore.createIndex(
                            'cemetery_id',
                            'cemetery_id'
                        )
                    }

                    if (!db.objectStoreNames.contains('uploaded_dates')) {
                        db.createObjectStore(
                            'uploaded_dates',
                            {
                                keyPath: 'cemetery_id'
                            }
                        )
                    }

                    if (!db.objectStoreNames.contains('uploaded_points')) {
                        db.createObjectStore(
                            'uploaded_points',
                            {
                                keyPath: 'cemetery_id'
                            }
                        )
                    }

                    if (!db.objectStoreNames.contains('uploaded_photos')) {
                        db.createObjectStore(
                            'uploaded_photos',
                            {
                                keyPath: 'cemetery_id'
                            }
                        )
                    }

                    if (!db.objectStoreNames.contains('uploaded_positions')) {
                        db.createObjectStore(
                            'uploaded_positions',
                            {
                                keyPath: 'cemetery_id'
                            }
                        )
                    }
                }
            })

            await this.initOPFS(userId)
            await this.initMetadata()
            await this.ensureCemeteryGuids()

            this.initialized = true
        } catch (error) {
            this.initialized = false
            console.error('Ошибка инициализации IndexedDB:', error)
            throw error
        }
    }

    private async ensureCemeteryGuids(): Promise<void> {
        this.checkDB()

        const tx = this.db!.transaction('cemetery', 'readwrite')
        const store = tx.objectStore('cemetery')
        const allCemeteries = await store.getAll()

        for (const cemetery of allCemeteries) {
            if (!cemetery.guid) {
                cemetery.guid = create_UUID()
                await store.put(cemetery)
            }
        }

        await tx.done
    }

    private async initOPFS(userId: number | null): Promise<void> {
        try {
            const root = await navigator.storage.getDirectory()
            const userDirName = `user_${userId || 'guest'}`

            await root.getDirectoryHandle(userDirName, {
                create: true
            })
        } catch (error) {
            console.error('Ошибка инициализации OPFS:', error)
        }
    }

    private async initMetadata(): Promise<void> {
        this.checkDB()

        const tx = this.db!.transaction('metadata', 'readwrite')
        const store = tx.objectStore('metadata')

        const metadata = await store.get('app')

        if (!metadata) {
            await store.put({
                id: 'app',
                lastSync: new Date(0),
                dbName: getDBName(this.currentUserId),
                dbVersion: DB_VERSION,
                userId: this.currentUserId
            })
        }

        await tx.done
    }

    private checkDB(): void {
        if (!this.db || this.db.transaction === undefined) {
            throw new Error('IndexedDB не инициализирован.')
        }
    }

    async getMetadata(): Promise<AppMetadata> {
        this.checkDB()

        return this.db!.get('metadata', 'app')
    }

    async updateMetadata(updates: Partial<AppMetadata>): Promise<void> {
        this.checkDB()

        const tx = this.db!.transaction('metadata', 'readwrite')
        const store = tx.objectStore('metadata')

        const metadata = await store.get('app')

        await store.put({
            ...metadata,
            ...updates,
            id: 'app',
            userId: this.currentUserId
        })

        await tx.done
    }

    async saveData<T>(storeName: string, data: T[]): Promise<void> {
        this.checkDB()

        try {
            const tx = this.db!.transaction(storeName, 'readwrite')
            const store = tx.objectStore(storeName)

            const itemsToStore = data
                .map((item) => this.cloneForIDB(item))
                .filter((item): item is T => item !== null)

            for (const item of itemsToStore) {
                await store.put(item)
            }

            await tx.done
        } catch (error) {
            if (getErrorName(error) === 'InvalidStateError') {
                await this.init(this.currentUserId)
                return this.saveData(storeName, data)
            }

            throw error
        }
    }

    async getData<T>(storeName: string): Promise<T[]> {
        this.checkDB()

        return this.db!.getAll(storeName)
    }

    async getByField<T>(
        storeName: string,
        fieldName: string,
        value: IDBValidKey
    ): Promise<T[]> {
        this.checkDB()

        return this.db!.getAllFromIndex(
            storeName,
            fieldName,
            IDBKeyRange.only(value)
        )
    }

    async saveMapPosition(
        cemeteryId: number,
        center: number[],
        zoom: number
    ): Promise<void> {
        this.checkDB()

        const tx = this.db!.transaction('uploaded_positions', 'readwrite')
        const store = tx.objectStore('uploaded_positions')

        await store.put({
            cemetery_id: cemeteryId,
            center_map: center,
            zoom_map: zoom
        })

        await tx.done
    }

    async getMapPosition(cemeteryId: number): Promise<UploadedPositions | null> {
        this.checkDB()

        const tx = this.db!.transaction('uploaded_positions', 'readonly')
        const store = tx.objectStore('uploaded_positions')

        return store.get(cemeteryId)
    }

    async updateCemeteryPoints(data: CemeteryPoint[]): Promise<void> {
        this.checkDB()

        const tx = this.db!.transaction('cemetery_points', 'readwrite')
        const store = tx.objectStore('cemetery_points')

        try {
            const itemsToStore = data
                .map((item) => this.cloneForIDB(item))
                .filter((item): item is CemeteryPoint => item !== null)

            for (const item of itemsToStore) {
                await store.put(item)
            }

            await tx.done
        } catch (error) {
            if (getErrorName(error) === 'QuotaExceededError') {
                throw new Error(
                    'Ошибка: закончилось место в базе данных! Выгрузите данные, чтобы освободить место.'
                )
            }

            throw error
        }
    }

    async saveFileToOPFS(
        file: File,
        cemeteryId: number,
        cardId: number
    ): Promise<string> {
        const userDirName = `user_${this.currentUserId || 'guest'}`
        const fileExt = file.name.split('.').pop() || 'jpg'
        const fileName = `${create_UUID()}.${fileExt}`

        if (typeof Worker !== 'undefined') {
            return new Promise<string>((resolve, reject) => {
                const worker = new SaveWorker()

                worker.postMessage([
                    file,
                    fileName,
                    userDirName,
                    cemeteryId,
                    cardId
                ])

                worker.onmessage = (event: MessageEvent<{
                    error?: string
                    fileName?: string
                }>) => {
                    if (event.data.error) {
                        reject(new Error(event.data.error))
                        return
                    }

                    if (!event.data.fileName) {
                        reject(new Error('Worker не вернул имя файла'))
                        return
                    }

                    resolve(event.data.fileName)
                }

                worker.onerror = (error) => {
                    console.error('Worker error:', error)
                    reject(error)
                }
            })
        }

        const root = await navigator.storage.getDirectory()
        const userDir = await root.getDirectoryHandle(userDirName, {
            create: true
        })
        const cemeteryDir = await userDir.getDirectoryHandle(
            `cemetery_${cemeteryId}`,
            {
                create: true
            }
        )
        const cardDir = await cemeteryDir.getDirectoryHandle(
            `card_${cardId}`,
            {
                create: true
            }
        )
        const fileHandle = await cardDir.getFileHandle(fileName, {
            create: true
        })
        const writable = await fileHandle.createWritable()

        await writable.write(file)
        await writable.close()

        return fileName
    }

    async checkFileExistsInOPFS(
        fileName: string,
        cemeteryId: number,
        cardId?: number
    ): Promise<boolean> {
        try {
            const root = await navigator.storage.getDirectory()
            const userDirName = `user_${this.currentUserId || 'guest'}`
            const userDir = await root.getDirectoryHandle(userDirName, {
                create: false
            })
            const cemeteryDir = await userDir.getDirectoryHandle(
                `cemetery_${cemeteryId}`,
                {
                    create: false
                }
            )

            if (!cardId) {
                return false
            }

            const cardDir = await cemeteryDir.getDirectoryHandle(
                `card_${cardId}`,
                {
                    create: false
                }
            )

            await cardDir.getFileHandle(fileName, {
                create: false
            })

            return true
        } catch {
            return false
        }
    }

    async getFileUrlFromOPFS(
        fileName: string,
        cemeteryId: number,
        cardId?: number
    ): Promise<File | null> {
        if (!cardId) {
            return null
        }

        try {
            const root = await navigator.storage.getDirectory()
            const userDirName = `user_${this.currentUserId || 'guest'}`
            const userDir = await root.getDirectoryHandle(userDirName, {
                create: false
            })
            const cemeteryDir = await userDir.getDirectoryHandle(
                `cemetery_${cemeteryId}`,
                {
                    create: false
                }
            )
            const cardDir = await cemeteryDir.getDirectoryHandle(
                `card_${cardId}`,
                {
                    create: false
                }
            )
            const fileHandle = await cardDir.getFileHandle(fileName, {
                create: false
            })

            return fileHandle.getFile()
        } catch (error) {
            if (getErrorName(error) !== 'NotFoundError') {
                console.error('Ошибка получения файла:', error)
            }

            return null
        }
    }

    async getPhotosSize(cemeteryId: number): Promise<number> {
        this.checkDB()

        try {
            const points = await this.getByField<CemeteryPoint>(
                'cemetery_points',
                'cemetery_id',
                cemeteryId
            )

            let totalSize = 0

            for (const point of points) {
                for (const photo of point.photos) {
                    const fileExists = await this.checkFileExistsInOPFS(
                        photo.name,
                        cemeteryId,
                        point.card_id
                    )

                    if (!fileExists) {
                        console.warn(
                            `Файл ${photo.name} не найден в OPFS для кладбища ${cemeteryId}, точки ${point.id}`
                        )
                        continue
                    }

                    const file = await this.getFileUrlFromOPFS(
                        photo.name,
                        cemeteryId,
                        point.card_id
                    )

                    if (file) {
                        totalSize += file.size
                    }
                }
            }

            return totalSize
        } catch (error) {
            console.error('Ошибка при подсчете объема фотографий:', error)
            return 0
        }
    }

    async deleteFileFromOPFS(
        fileName: string,
        cemeteryId: number,
        cardId: number
    ): Promise<void> {
        try {
            const root = await navigator.storage.getDirectory()
            const userDirName = `user_${this.currentUserId || 'guest'}`
            const userDir = await root.getDirectoryHandle(userDirName, {
                create: false
            })
            const cemeteryDir = await userDir.getDirectoryHandle(
                `cemetery_${cemeteryId}`,
                {
                    create: false
                }
            )
            const cardDir = await cemeteryDir.getDirectoryHandle(
                `card_${cardId}`,
                {
                    create: false
                }
            )

            await cardDir.removeEntry(fileName)
        } catch (error) {
            if (getErrorName(error) === 'NotFoundError') {
                return
            }

            console.error('Ошибка удаления файла:', error)
            throw error
        }
    }

    async saveUploadDate(
        cemeteryId: number,
        photoCount: number,
        date: Date = new Date()
    ): Promise<void> {
        this.checkDB()

        const formattedDate = date.toLocaleString('ru-RU', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        }).replace(',', ' в ')

        const tx = this.db!.transaction('uploaded_dates', 'readwrite')

        await tx.objectStore('uploaded_dates').put({
            cemetery_id: cemeteryId,
            photo_count: photoCount,
            uploaded_last_date: formattedDate
        })

        await tx.done
    }

    async getUploadDate(cemeteryId: number): Promise<string | null> {
        this.checkDB()

        const record = await this.db!.get('uploaded_dates', cemeteryId) as UploadedDates | undefined

        return record?.uploaded_last_date || null
    }

    async getUploadPhotosCount(cemeteryId: number): Promise<number> {
        this.checkDB()

        const record = await this.db!.get('uploaded_dates', cemeteryId) as UploadedDates | undefined

        return record?.photo_count || 0
    }

    async removeUploadDate(cemeteryId: number): Promise<void> {
        this.checkDB()

        const tx = this.db!.transaction('uploaded_dates', 'readwrite')

        await tx.objectStore('uploaded_dates').delete(cemeteryId)
        await tx.done
    }

    async savetUploadedPoints(
        cemeteryId: number,
        pointIds: number[]
    ): Promise<void> {
        this.checkDB()

        const tx = this.db!.transaction('uploaded_points', 'readwrite')
        const store = tx.objectStore('uploaded_points')

        await store.put({
            cemetery_id: cemeteryId,
            point_ids: pointIds,
            last_upload: new Date()
        })

        await tx.done
    }

    async getUploadedPoints(cemeteryId: number): Promise<UploadedPoints | null> {
        this.checkDB()

        const record = await this.db!.get('uploaded_points', cemeteryId)

        return record || null
    }

    async removePointFromUploaded(
        cemeteryId: number,
        pointId: number
    ): Promise<void> {
        this.checkDB()

        if (!this.db!.objectStoreNames.contains('uploaded_points')) {
            console.warn('Хранилище uploaded_points не существует')
            return
        }

        const tx = this.db!.transaction('uploaded_points', 'readwrite')
        const store = tx.objectStore('uploaded_points')
        const record = await store.get(cemeteryId) as UploadedPoints | undefined

        if (record) {
            const updatedPoints = record.point_ids.filter((id: number) => {
                return id !== pointId
            })

            await store.put({
                ...record,
                point_ids: updatedPoints
            })
        }

        await tx.done
    }

    async addUploadedPhotos(
        cemeteryId: number,
        photoNames: string[]
    ): Promise<void> {
        this.checkDB()

        const tx = this.db!.transaction('uploaded_photos', 'readwrite')
        const store = tx.objectStore('uploaded_photos')
        const existing = await store.get(cemeteryId) as UploadedPhotos | undefined

        const updatedPhotos = existing
            ? Array.from(new Set([
                ...existing.photo_names,
                ...photoNames
            ]))
            : photoNames

        await store.put({
            cemetery_id: cemeteryId,
            photo_names: updatedPhotos,
            last_upload: new Date()
        })

        await tx.done
    }

    async addUploadedPhoto(
        cemeteryId: number,
        photoName: string
    ): Promise<void> {
        this.checkDB()

        const tx = this.db!.transaction('uploaded_photos', 'readwrite')
        const store = tx.objectStore('uploaded_photos')
        const existingData = await store.get(cemeteryId) as UploadedPhotos | undefined
        const photoNames = existingData?.photo_names || []

        if (!photoNames.includes(photoName)) {
            photoNames.push(photoName)

            await store.put({
                cemetery_id: cemeteryId,
                photo_names: photoNames,
                last_upload: new Date()
            })
        }

        await tx.done
    }

    async getUploadedPhotos(cemeteryId: number): Promise<UploadedPhotos | null> {
        this.checkDB()

        if (!this.db!.objectStoreNames.contains('uploaded_photos')) {
            return null
        }

        const record = await this.db!.get('uploaded_photos', cemeteryId)

        return record || null
    }

    async isPhotoUploaded(
        cemeteryId: number,
        photoName: string
    ): Promise<boolean> {
        const record = await this.getUploadedPhotos(cemeteryId)

        return record
            ? record.photo_names.includes(photoName)
            : false
    }

    async removeUploadedPhoto(
        cemeteryId: number,
        photoName: string
    ): Promise<void> {
        this.checkDB()

        const tx = this.db!.transaction('uploaded_photos', 'readwrite')
        const store = tx.objectStore('uploaded_photos')
        const record = await store.get(cemeteryId) as UploadedPhotos | undefined

        if (record) {
            const updatedPhotos = record.photo_names.filter((name: string) => {
                return name !== photoName
            })

            await store.put({
                ...record,
                photo_names: updatedPhotos
            })
        }

        await tx.done
    }

    async deleteAllPointsForCemetery(cemeteryId: number): Promise<void> {
        this.checkDB()

        try {
            const points = await this.getByField<CemeteryPoint>(
                'cemetery_points',
                'cemetery_id',
                cemeteryId
            )

            for (const point of points) {
                for (const photo of point.photos) {
                    await this.deleteFileFromOPFS(
                        photo.name,
                        cemeteryId,
                        point.card_id
                    )
                }

                await this.deletePointFromIndexedDB(point.id)
                await this.removePointFromUploaded(cemeteryId, point.id)
            }

            const tx = this.db!.transaction('uploaded_photos', 'readwrite')
            const store = tx.objectStore('uploaded_photos')

            await store.delete(cemeteryId)
            await tx.done

            await this.deleteCemeteryDirectory(cemeteryId)
        } catch (error) {
            console.error('Ошибка удаления точек на кладбище:', error)
            throw error
        }
    }

    private async deleteCemeteryDirectory(cemeteryId: number): Promise<void> {
        const delay = (ms: number) => new Promise((resolve) => {
            setTimeout(resolve, ms)
        })

        try {
            const root = await navigator.storage.getDirectory()
            const userDirName = `user_${this.currentUserId || 'guest'}`

            try {
                const userDir = await root.getDirectoryHandle(userDirName, {
                    create: false
                })

                await delay(50)

                try {
                    const cemeteryDir = await userDir.getDirectoryHandle(
                        `cemetery_${cemeteryId}`,
                        {
                            create: false
                        }
                    )

                    await delay(50)

                    const subdirs: string[] = []

                    for await (const entry of cemeteryDir.values()) {
                        if (entry.kind === 'directory') {
                            subdirs.push(entry.name)
                        }
                    }

                    for (const subdir of subdirs) {
                        try {
                            await cemeteryDir.removeEntry(subdir, {
                                recursive: true
                            })
                            await delay(30)
                        } catch (subdirError) {
                            console.warn(
                                `Не удалось удалить подпапку ${subdir}:`,
                                subdirError
                            )
                            await delay(30)
                        }
                    }

                    await delay(100)

                    let attempts = 3

                    while (attempts > 0) {
                        try {
                            await userDir.removeEntry(
                                `cemetery_${cemeteryId}`,
                                {
                                    recursive: true
                                }
                            )
                            break
                        } catch (removeError) {
                            attempts--

                            if (getErrorName(removeError) === 'NotFoundError') {
                                break
                            }

                            console.warn(
                                `Попытка ${4 - attempts} удаления папки кладбища ${cemeteryId}:`,
                                removeError
                            )

                            if (attempts === 0) {
                                throw removeError
                            }

                            await delay(200 * (4 - attempts))
                        }
                    }
                } catch (cemeteryError) {
                    if (getErrorName(cemeteryError) !== 'NotFoundError') {
                        console.error(
                            `Ошибка доступа к папке кладбища ${cemeteryId}:`,
                            cemeteryError
                        )
                        throw cemeteryError
                    }
                }
            } catch (userDirError) {
                if (getErrorName(userDirError) !== 'NotFoundError') {
                    console.error(
                        `Ошибка доступа к папке пользователя ${userDirName}:`,
                        userDirError
                    )
                    throw userDirError
                }
            }
        } catch (error) {
            console.error('Общая ошибка при удалении папки кладбища:', error)
            throw error
        }
    }

    async deletePointFromIndexedDB(pointId: number): Promise<void> {
        this.checkDB()

        const tx = this.db!.transaction('cemetery_points', 'readwrite')
        const store = tx.objectStore('cemetery_points')

        await store.delete(pointId)
        await tx.done
    }

    async getTotalPhotosCount(cemeteryId?: number): Promise<number> {
        this.checkDB()

        try {
            const points = cemeteryId
                ? await this.getByField<CemeteryPoint>(
                    'cemetery_points',
                    'cemetery_id',
                    cemeteryId
                )
                : await this.getData<CemeteryPoint>('cemetery_points')

            const photoCounts: number[] = await Promise.all(
                points.map(async (point) => {
                    if (!point.photos?.length) {
                        return 0
                    }

                    const existingPhotos: number[] = await Promise.all(
                        point.photos.map(async (photo) => {
                            const exists = await this.checkFileExistsInOPFS(
                                photo.name,
                                point.cemetery_id,
                                point.card_id
                            )

                            return exists ? 1 : 0
                        })
                    )

                    return existingPhotos.reduce<number>((sum, exists) => {
                        return sum + exists
                    }, 0)
                })
            )

            return photoCounts.reduce<number>((sum, count) => {
                return sum + count
            }, 0)
        } catch (error) {
            console.error('Ошибка при подсчете фотографий:', error)
            return 0
        }
    }

    private cloneForIDB<T>(data: T): T | null {
        try {
            return JSON.parse(JSON.stringify(data)) as T
        } catch (error) {
            console.error('Ошибка клонирования:', error)
            return null
        }
    }
}

export async function getBurialsForCard(cardId: number): Promise<CemeteryBurial[]> {
    return db.getByField<CemeteryBurial>(
        'cemetery_burials',
        'card_id',
        cardId
    )
}

export async function getPointsOfUserForCard(cardId: number): Promise<CemeteryPointOfUser[]> {
    return db.getByField<CemeteryPointOfUser>(
        'cemetery_points_of_user',
        'card_id',
        cardId
    )
}

export async function getPointsForCemetery(cemeteryId: number): Promise<CemeteryPoint[]> {
    return db.getByField<CemeteryPoint>(
        'cemetery_points',
        'cemetery_id',
        cemeteryId
    )
}

export async function getBurialsForCemetry(cemeteryId: number): Promise<CemeteryBurial[]> {
    return db.getByField<CemeteryBurial>(
        'cemetery_burials',
        'cemetery_id',
        cemeteryId
    )
}

export const db = new IndexedDB()